/*
 * gs-facet-ui.js — Goods Scene
 * Presentation helpers for the filter bar. Nothing here filters, sorts or
 * builds a URL: Shopify and Dawn's assets/facets.js still do all of that.
 *
 *  1. VALUE COUNTS. Dawn prints a value as one text node, "HEPA Filter (41)".
 *     To set the count apart (muted, right-aligned) it is wrapped in its own
 *     span. The TEXT of the row is left exactly as it was — still
 *     "HEPA Filter (41)" — so assets/gs-facet-search.js, which reads that text
 *     and strips the count, is unaffected. Without this script the row simply
 *     shows Dawn's original text.
 *
 *  2. SORT. The desktop "Sort by" control is a native <select>, whose open
 *     menu is drawn by the browser and cannot be styled. A button and a list
 *     are drawn in its place. THE <select> STAYS IN THE FORM AND STAYS THE
 *     SOURCE OF TRUTH: choosing an option sets select.value and fires the same
 *     `input` event a real change fires, so Dawn's own handler builds the URL
 *     and refreshes the grid exactly as before. No sort value, parameter or
 *     request is produced here. The select is only hidden while the custom
 *     control is in place (.gs-sort-native), so if this script does not run the
 *     native select is what the shopper sees.
 *     The select inside the mobile "Filter and sort" drawer is NOT replaced: on
 *     a phone the native picker is the better control. It is restyled in CSS.
 *
 * LIFECYCLE. Dawn replaces the innerHTML of each .js-filter and of .sorting on
 * every filter change. Observers re-apply both helpers; each is idempotent, so
 * a second pass writes nothing.
 */
(function () {
  'use strict';

  /* ---------- 1. value counts ---------- */

  var COUNT_RE = /^([\s\S]*\S)\s*\(([\d.,\s]+)\)\s*$/;

  function splitCount(span) {
    if (span.firstElementChild) return; // already done, or not plain text
    var match = COUNT_RE.exec(span.textContent || '');
    if (!match) return;
    var number = match[2].replace(/\s+/g, '');

    var label = document.createElement('span');
    label.className = 'gs-facet-label';
    label.textContent = match[1];

    var count = document.createElement('span');
    count.className = 'gs-facet-count';
    count.setAttribute('data-count', number);
    count.textContent = '(' + number + ')';

    span.textContent = '';
    span.appendChild(label);
    span.appendChild(document.createTextNode(' '));
    span.appendChild(count);
    span.classList.add('gs-facet-text');
  }

  function splitAll(root) {
    var spans = root.querySelectorAll('.facet-checkbox__text');
    for (var i = 0; i < spans.length; i++) splitCount(spans[i]);
  }

  /* ---------- 2. sort ---------- */

  var uid = 0;
  var openSort = null; // the one open control, if any
  var refocus = false; // put focus back on the button after Dawn redraws it

  var CARET =
    '<svg class="gs-sort__caret" aria-hidden="true" focusable="false" viewBox="0 0 10 6" width="10" height="6">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/>' +
    '</svg>';

  function optionsOf(sort) {
    return sort.list.querySelectorAll('.gs-sort__option');
  }

  function setActive(sort, index) {
    var options = optionsOf(sort);
    if (!options.length) return;
    if (index < 0) index = 0;
    if (index > options.length - 1) index = options.length - 1;
    for (var i = 0; i < options.length; i++) {
      options[i].classList.toggle('is-active', i === index);
    }
    sort.active = index;
    sort.list.setAttribute('aria-activedescendant', options[index].id);
    if (options[index].scrollIntoView) options[index].scrollIntoView({ block: 'nearest' });
  }

  function closeSort(sort, focusButton) {
    if (!sort || sort.list.hidden) return;
    sort.list.hidden = true;
    sort.button.setAttribute('aria-expanded', 'false');
    sort.root.classList.remove('is-open');
    if (openSort === sort) openSort = null;
    if (focusButton) sort.button.focus();
  }

  function openSortList(sort) {
    if (openSort && openSort !== sort) closeSort(openSort, false);
    sort.list.hidden = false;
    sort.button.setAttribute('aria-expanded', 'true');
    sort.root.classList.add('is-open');
    openSort = sort;
    setActive(sort, Math.max(sort.select.selectedIndex, 0));
    sort.list.focus();
  }

  function choose(sort, index) {
    var options = optionsOf(sort);
    var option = options[index];
    if (!option) return;
    var value = option.getAttribute('data-value');
    closeSort(sort, true);
    if (sort.select.value === value) return;

    // The native select is the source of truth. Set it, then let Dawn react to
    // the very event a real change would have fired.
    sort.select.value = value;
    for (var i = 0; i < options.length; i++) {
      var on = i === index;
      options[i].setAttribute('aria-selected', on ? 'true' : 'false');
      options[i].classList.toggle('is-selected', on);
    }
    sort.value.textContent = option.textContent;
    refocus = true;
    sort.select.dispatchEvent(new Event('input', { bubbles: true }));
    sort.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function buildSort(select) {
    var native = select.closest('.select');
    if (!native || native.classList.contains('gs-sort-native')) return;
    if (!select.options.length) return;

    var id = 'GsSort-' + ++uid;
    var labelEl = select.id ? document.querySelector('label[for="' + select.id + '"]') : null;
    var labelText = labelEl
      ? labelEl.textContent.replace(/\s+/g, ' ').trim()
      : (window.gsT ? window.gsT('facets.sort_by') : '');

    var root = document.createElement('div');
    root.className = 'gs-sort';

    var button = document.createElement('button');
    button.type = 'button'; // never submits the facet form
    button.className = 'gs-sort__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', id);

    var value = document.createElement('span');
    value.className = 'gs-sort__value';
    button.appendChild(value);
    button.insertAdjacentHTML('beforeend', CARET);

    var list = document.createElement('ul');
    list.className = 'gs-sort__list list-unstyled';
    list.id = id;
    list.setAttribute('role', 'listbox');
    list.setAttribute('tabindex', '-1');
    list.setAttribute('aria-label', labelText);
    list.hidden = true;

    for (var i = 0; i < select.options.length; i++) {
      var source = select.options[i];
      var item = document.createElement('li');
      var selected = i === select.selectedIndex;
      item.className = 'gs-sort__option' + (selected ? ' is-selected' : '');
      item.id = id + '-' + i;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.setAttribute('data-value', source.value);
      item.setAttribute('data-index', String(i));
      item.textContent = source.text.replace(/\s+/g, ' ').trim();
      list.appendChild(item);
      if (selected) value.textContent = item.textContent;
    }
    button.setAttribute('aria-label', labelText + ' ' + value.textContent);

    root.appendChild(button);
    root.appendChild(list);

    var sort = { root: root, button: button, list: list, value: value, select: select, active: 0 };

    button.addEventListener('click', function () {
      if (list.hidden) openSortList(sort);
      else closeSort(sort, false);
    });

    button.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openSortList(sort);
      }
    });

    list.addEventListener('keydown', function (event) {
      var key = event.key;
      if (key === 'ArrowDown') {
        event.preventDefault();
        setActive(sort, sort.active + 1);
      } else if (key === 'ArrowUp') {
        event.preventDefault();
        setActive(sort, sort.active - 1);
      } else if (key === 'Home') {
        event.preventDefault();
        setActive(sort, 0);
      } else if (key === 'End') {
        event.preventDefault();
        setActive(sort, optionsOf(sort).length - 1);
      } else if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
        event.preventDefault();
        choose(sort, sort.active);
      } else if (key === 'Escape' || key === 'Esc') {
        event.preventDefault();
        event.stopPropagation(); // Dawn's Escape handler closes filter panels
        closeSort(sort, true);
      } else if (key === 'Tab') {
        closeSort(sort, false);
      }
    });

    // Keyup must not reach Dawn's Escape listener either.
    list.addEventListener('keyup', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') event.stopPropagation();
    });

    list.addEventListener('click', function (event) {
      var item = event.target && event.target.closest ? event.target.closest('.gs-sort__option') : null;
      if (item) choose(sort, parseInt(item.getAttribute('data-index'), 10));
    });

    list.addEventListener('mousemove', function (event) {
      var item = event.target && event.target.closest ? event.target.closest('.gs-sort__option') : null;
      if (!item) return;
      var index = parseInt(item.getAttribute('data-index'), 10);
      if (index !== sort.active) setActive(sort, index);
    });

    root.addEventListener('focusout', function (event) {
      if (event.relatedTarget && root.contains(event.relatedTarget)) return;
      closeSort(sort, false);
    });

    if (labelEl && !labelEl.gsSortBound) {
      labelEl.gsSortBound = true;
      labelEl.addEventListener('click', function (event) {
        var current = document.querySelector('.gs-sort__button');
        if (!current) return; // custom control gone: let the label do its job
        event.preventDefault();
        current.focus();
      });
    }

    native.parentNode.insertBefore(root, native.nextSibling);
    native.classList.add('gs-sort-native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    if (refocus) {
      refocus = false;
      button.focus();
    }
  }

  function buildAllSorts() {
    // Desktop only: the drawer's select has no .facet-filters__sort class.
    var selects = document.querySelectorAll('select.facet-filters__sort');
    for (var i = 0; i < selects.length; i++) buildSort(selects[i]);
  }

  document.addEventListener('click', function (event) {
    if (openSort && !openSort.root.contains(event.target)) closeSort(openSort, false);
  });

  /* ---------- lifecycle ---------- */

  function observeOnce(el, callback, options) {
    if (!el || el.gsFacetUiObserved) return;
    el.gsFacetUiObserved = true;
    new MutationObserver(callback).observe(el, options);
  }

  function scan() {
    var i;
    var filters = document.querySelectorAll('.js-filter');
    for (i = 0; i < filters.length; i++) {
      (function (filter) {
        observeOnce(
          filter,
          function () {
            splitAll(filter);
          },
          { childList: true, subtree: true }
        );
        splitAll(filter);
      })(filters[i]);
    }

    var sorting = document.querySelectorAll('.sorting');
    for (i = 0; i < sorting.length; i++) {
      observeOnce(sorting[i], buildAllSorts, { childList: true });
    }
    buildAllSorts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', scan);
})();